import { FC, useEffect, useRef, useState } from 'react'

import {
  useAnalytics,
  useFbp,
  usePinterest,
  useRedirect,
  useSetting,
  useSnapChat,
  useTtq
} from '@ui/hooks'
import css from 'styled-jsx/css'
import { REMOVE_ALL_AND_ADD, useNotiContext } from './Notification'
import {
  genSid,
  useCheckoutService,
  useLocalIsBuyNow,
  useLocalIsPuchase,
  useLocalSessionId
} from '@libs/client'
import { cartMapping } from '@ui/helpers'
import { useGtag } from '@ui/hooks/useGtag'
import getLogRocket from '@ui/analytics/logRocket'
import { logger } from '@ui/analytics'

interface IPaypalButton {
  paypalEx: S3Types.paygate
  isBuyNow?: boolean
  isPurchase?: boolean
}

const PaypalButton: FC<IPaypalButton> = ({ paypalEx, isBuyNow, isPurchase }) => {
  const redirect = useRedirect()
  const [sellpageSetting] = useSetting('sellpage')
  const { notiDispatch } = useNotiContext()
  const { initPaypal, capturePaypal, cancelPaypal, errorPaypal } = useCheckoutService({
    cartMapping: (cart) => cartMapping(cart, sellpageSetting?.variants?.items ?? {})
  })
  const [, setLocalSissionId] = useLocalSessionId()
  const { fbpTrackInitiateCheckout, fbpTrackAddPaymentInfo, fbpTrackPurchase } = useFbp()
  const { pinterestTrackPurchase } = usePinterest()
  const {
    snapchatTrackInitiateCheckout,
    snapchatTrackAddPaymentInfo,
    snapchatTrackPurchase
  } = useSnapChat()
  const {
    ttqTrackInitiateCheckout,
    ttqTrackCheckout,
    ttqTrackAddBilling,
    ttqTrackPurchase
  } = useTtq()
  const {
    gtagTrackBeginCheckout,
    gtagTrackAddPaymentInfo,
    gtagTrackAddShippingInfo,
    gtagTrackPurchase,
    gtagTrackConversionPurchase
  } = useGtag()
  const [, setLocalIsPuchase] = useLocalIsPuchase()
  const analytics = useAnalytics()
  const googleConversion = `${analytics?.google_conversion_id || ''}/${
    analytics?.google_conversion_label || ''
  }`

  const currentOrder = useRef<Types.Order | null>(null)
  const [locaIsBuyNow] = useLocalIsBuyNow()

  const [isLoading, setIsLoading] = useState(true)

  const { credential_clientid } = paypalEx

  useEffect(() => {
    if (!(window as any).paypal) {
      initPaypalScript()
    }

    const interval = setInterval(() => {
      if ((window as any).paypal) {
        setIsLoading(false)
        // @ts-ignore
        window.paypal
          .Buttons({
            style: style,
            createOrder: createOrder,
            onApprove: onApprove,
            onShippingChange: onShippingChange,
            onCancel: onCancel,
            onError: onError
          })
          .render('.js-paypal-button')
          .catch(console.warn)
        clearInterval(interval)
      } else {
        initPaypalScript()
      }
    }, 300)
    return () => {
      clearInterval(interval)
    }
  }, [])

  function initPaypalScript() {
    const PAYPAL_SCRIPT = `https://www.paypal.com/sdk/js?client-id=${credential_clientid}&disable-funding=paylater`
    const script = document.createElement('script')
    script.setAttribute('src', PAYPAL_SCRIPT)
    document.head.appendChild(script)
  }

  async function createOrder() {
    getLogRocket().logRocket?.init()
    console.log('createOrder')

    if (!isPurchase) {
      return notiDispatch({
        payload: {
          content: `You cannot checkout. This page is for preview only.`,
          type: 'is-danger'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    try {
      // LogRocket.track('PaypalOrderCreated')
      const result = await initPaypal(isBuyNow) // Important: must be return an order_id for `createOrder` function

      if (result?.status === 'COMPLETED') {
        notiDispatch({
          type: REMOVE_ALL_AND_ADD,
          payload: {
            content: 'Your payment was successfully.',
            type: 'is-success'
          }
        })
        redirect('/thankyou')
      }
      if (!locaIsBuyNow) {
        fbpTrackInitiateCheckout(result?.order?.items, result?.order?.items_amount)
        snapchatTrackInitiateCheckout(result?.order?.items, result?.order?.items_amount)
        ttqTrackInitiateCheckout(result?.order?.items, result?.order?.items_amount)
        ttqTrackCheckout(result?.order?.items, result?.order?.items_amount)
        gtagTrackBeginCheckout(result?.order?.items, result?.order?.items_amount)
      }

      logger.logProductEvent('checkout_paypal')

      currentOrder.current = result?.order ?? null

      return result?.orderId
    } catch (err) {
      if (err?.message && err?.status) {
        notiDispatch({
          payload: {
            content: err.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      } else {
        notiDispatch({
          payload: {
            content: err?.response?.data?.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      }
    }
  }

  async function onApprove(data: any, actions: any) {
    console.log('onApprove')
    fbpTrackAddPaymentInfo(currentOrder.current?.items, currentOrder.current?.items_amount)
    snapchatTrackAddPaymentInfo(currentOrder.current?.items, currentOrder.current?.items_amount)
    ttqTrackAddBilling(currentOrder.current?.items, currentOrder.current?.items_amount)
    gtagTrackAddPaymentInfo(
      currentOrder.current?.items,
      currentOrder.current?.items_amount,
      'Paypal'
    )
    gtagTrackAddShippingInfo(currentOrder.current?.items, currentOrder.current?.items_amount)

    logger.logProductEvent('paid_paypal')
    try {
      const result = await capturePaypal(data, actions)

      notiDispatch({
        type: REMOVE_ALL_AND_ADD,
        payload: {
          content: 'Your payment was successfully.',
          type: 'is-success'
        }
      })

      // Analytics.track('PURCHASE', {
      //   extra: { orderId: this.order.id }
      // })
      // LogRocket.track('Purchased')
      // LogRocket.track('PaypalOrderPurchased')

      fbpTrackPurchase(result?.order.items, result?.order.amount)
      pinterestTrackPurchase(result?.order.amount)
      snapchatTrackPurchase(result?.order.items, result?.order.amount)
      ttqTrackPurchase(result?.order.items, result?.order.amount)
      gtagTrackPurchase(result?.order)
      gtagTrackConversionPurchase(googleConversion, result?.order)
      setLocalSissionId(genSid())
      setLocalIsPuchase(true)
      logger.logProductEvent('payment_success')

      redirect('/thankyou')
    } catch (err) {
      if (err?.message && err?.status) {
        notiDispatch({
          payload: {
            content: err.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      } else {
        notiDispatch({
          payload: {
            content: err?.response?.data?.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      }
      logger.logProductEvent('payment_failure')
    }
  }

  async function onShippingChange(shipping: any) {
    console.log('Shipping has been changed', shipping)
  }

  function onError(err: Error) {
    console.warn('Error while processing the payment with Paypal', err)
    errorPaypal()
  }

  function onCancel(data: any) {
    cancelPaypal(data)
  }

  return (
    <>
      <style jsx>{paypalStyle}</style>
      <div className="paypal-button">
        {isLoading ? (
          <button className="loading-button button is-fullwidth is-warning is-loading mb-1" />
        ) : null}
        <div className="js-paypal-button" />
      </div>
    </>
  )
}

export default PaypalButton

const style = {
  color: 'gold',
  height: 55,
  layout: 'horizontal',
  size: 'medium',
  shape: 'rect',
  tagline: false,
  fundingicons: 'false'
}

const paypalStyle = css`
  .paypal-button {
    .loading-button {
      background-color: #ffc43a !important;
      height: 55px;
    }
  }
`
